import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { OrderWithRelations } from "@ckgo2/shared/types";
import { Search, ShoppingCart, Eye, Phone, Mail, Calendar, LayoutGrid, List, Plus } from "lucide-react";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  inquiry: { label: "Poptávka", className: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300" },
  quoted: { label: "Naceneno", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  confirmed: { label: "Potvrzeno", className: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  deposit_paid: { label: "Záloha", className: "bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400" },
  fully_paid: { label: "Zaplaceno", className: "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400" },
  completed: { label: "Dokončeno", className: "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400" },
  cancelled: { label: "Zrušeno", className: "bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400" },
};

export default function OrdersPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["orders"],
    queryFn: () => api.get<ApiResponse<OrderWithRelations[]>>("/api/admin/orders"),
  });

  const orders = data?.data || [];

  // Filter orders
  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.customer?.firstName?.toLowerCase().includes(search.toLowerCase()) ||
      order.customer?.lastName?.toLowerCase().includes(search.toLowerCase()) ||
      order.customer?.email?.toLowerCase().includes(search.toLowerCase()) ||
      order.trip?.title?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (error) {
    return (
      <div className="card text-center text-error-600">
        Chyba při načítání objednávek. Zkuste to prosím znovu.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Objednávky
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Správa poptávek a objednávek
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link to="/orders/new" className="btn-primary">
            <Plus className="h-5 w-5" />
            Nová objednávka
          </Link>
          <Link to="/orders" className="btn-ghost">
            <List className="h-5 w-5" />
            Tabulka
          </Link>
          <Link to="/orders/kanban" className="btn-ghost">
            <LayoutGrid className="h-5 w-5" />
            Kanban
          </Link>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Nové poptávky</p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
            {orders.filter(o => o.status === "inquiry").length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Čekají na platbu</p>
          <p className="mt-1 text-2xl font-bold text-warning-600">
            {orders.filter(o => o.status === "confirmed" || o.status === "deposit_paid").length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Zaplaceno</p>
          <p className="mt-1 text-2xl font-bold text-success-600">
            {orders.filter(o => o.status === "fully_paid").length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Celková hodnota</p>
          <p className="mt-1 text-2xl font-bold text-primary-600">
            {formatPrice(orders.reduce((sum, o) => sum + (o.totalPrice || 0), 0))}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Hledat podle jména, emailu nebo zájezdu..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">Všechny stavy</option>
            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>
                {config.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-gray-500">
            <ShoppingCart className="mb-2 h-12 w-12 text-gray-300" />
            <p>Žádné objednávky nenalezeny</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Zákazník
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Zájezd
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Termín
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Cena
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredOrders.map((order) => {
                const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.inquiry;
                return (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {order.customer?.firstName} {order.customer?.lastName}
                        </p>
                        <div className="mt-1 flex items-center gap-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {order.customer?.email}
                          </span>
                          {order.customer?.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {order.customer.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900 dark:text-white">
                        {order.trip?.title || "-"}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {order.tripDate ? (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <Calendar className="h-4 w-4" />
                          {formatDate(order.tripDate.startDate)}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatPrice(order.totalPrice || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`badge ${statusConfig.className}`}>
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end">
                        <Link
                          to={`/orders/${order.id}`}
                          className="btn-ghost btn-sm"
                        >
                          <Eye className="h-4 w-4" />
                          Detail
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
